import { IApplicationState } from "./types";
import { ActionTree } from "vuex";
import { IRootState } from "@/store/types";
import { APPLICATION_MUTATIONS } from "./mutations";
import api from "@/api/signup/api";
import { ADDRESS_LOOKUP, emptyAddress } from "@/store/addressLookup";
import { UI } from "@/store/ui";
import { ITimelineItem } from "../task/types";
import { ERROR_MESSAGES } from "@/constants/errors";

export namespace APPLICATION_ACTIONS {
  export const TOGGLE_IS_LOADING = "toggleLoading";
  export const CHANGE_CREDIT_LIMIT = "changeCreditLimit";
  export const LOAD_APPLICATION_LIST = "loadApplicationList";
  export const LOAD_COMPLETED_APPLICATION_LIST = "loadCompletedApplicationList";
  export const LOAD_MY_APPLICATION_LIST = "loadMyApplicationList";
  export const LOAD_DRAFT_LIST = "loadDraftList";
  export const LOAD_APPLICATION = "loadApplication";
  export const RESET_APPLICATION_DATA_STATE = "resetApplicationDataState";
  export const PRE_POPULATE_ADDRESS = "prePopulateAddresses";
  export const UPDATE_APPLICATION = "updateApplication";
  export const CANCEL_APPLICATION = "cancelApplication";
  export const UPDATE_IFCS_DETAILS = "updateIfcsDetails";
  export const SHOW_REQUEST_FOR_CALLBACK = "showRequestForCallback";
  export const DECLINE_CALLBACK_OUTCOME = "declineCallbackOutcome";
  export const CREDIT_TEAM_APPROVE_APPLICATION = "creditTeamApproveApplication";
  export const CREDIT_TEAM_DECLINE_APPLICATION = "creditTeamDeclineApplication";
  export const CREDIT_TEAM_NEED_PG = "creditTeamNeedPg";
  export const COMPLETED_APPLICATION = "completedApplication";
  export const LOAD_PHYSICAL_ADDRESS_DETAILS = "loadPhysicalAddressDetails";
  export const LOAD_DELIVERY_ADDRESS_DETAILS = "loadDeliveryAddressDetails";
  export const SHOW_CREDIT_OUTCOME = "showCreditOutcome";
  export const SHOW_CARD_ACCOUNT_SETUP = "showCardAccountSetup";
  export const SHOW_MODIFY_ACCOUNT_NUMBER = "showModifyAccountNumber";
  export const SHOW_JDE_ACCOUNT_SETUP = "showJdeAccountSetup";
  export const SHOW_FOLLOW_UP_PERSONAL_GUARANTEE =
    "showFollowUpPersonalGuarantee";
  export const PERSONAL_GUARANTEE_OBTAINED = "personalGuaranteeObtained";
  export const CARD_ACCOUNT_SET_UP_COMPLETE = "cardAccountSetupComplete";
  export const MODIFY_ACCOUNT_NUMBER_COMPLETE = "modifyAccountNumberComplete";
  export const JDE_ACCOUNT_SET_UP_COMPLETE = "jdeAccountSetupComplete";
  export const OPEN_ASSIGN_TO_ME_CONFIRMATION = "openAssignToMeConfirmation";
  export const CLOSE_ASSIGN_TO_ME_CONFIRMATION = "closeAssignToMeConfirmation";
  export const SET_MY_APPLICATION_STATUS_FILTERS =
    "setMyApplicationStatusFilters";
  export const SET_ALL_APPLICATION_STATUS_FILTERS =
    "setAllApplicationStatusFilters";
  export const SET_COMPLETED_STATUS_FILTERS = "setCompletedStatusFilters";
}

export const actions: ActionTree<IApplicationState, IRootState> = {
  [APPLICATION_ACTIONS.OPEN_ASSIGN_TO_ME_CONFIRMATION]({ commit }) {
    commit(APPLICATION_MUTATIONS.TOGGLE_ASSIGN_TO_ME_CONFIRMATION, true);
  },
  [APPLICATION_ACTIONS.CLOSE_ASSIGN_TO_ME_CONFIRMATION]({ commit }) {
    commit(APPLICATION_MUTATIONS.TOGGLE_ASSIGN_TO_ME_CONFIRMATION, false);
  },
  [APPLICATION_ACTIONS.TOGGLE_IS_LOADING](
    { commit },
    isLoading: boolean
  ): void {
    commit(APPLICATION_MUTATIONS.TOGGLE_IS_LOADING, isLoading);
  },
  async [APPLICATION_ACTIONS.RESET_APPLICATION_DATA_STATE]({
    commit,
  }): Promise<void> {
    await commit(APPLICATION_MUTATIONS.RESET_APPLICATION_DATA_STATE);
  },
  async [APPLICATION_ACTIONS.LOAD_APPLICATION_LIST](
    { commit, dispatch },
    { statuses, pagination }
  ): Promise<void> {
    commit(APPLICATION_MUTATIONS.TOGGLE_IS_LOADING, true);

    try {
      let data = [];
      let total = 0;

      // Don't bother searching if no statuses are provided.
      if (statuses && statuses.length > 0) {
        const response = await api.getApplications(statuses, pagination);
        data = response.data.data;
        total = response.data.pagination.total;
      }

      commit(APPLICATION_MUTATIONS.SET_APPLICATION_LIST, data);
      commit(APPLICATION_MUTATIONS.SET_APPLICATION_LIST_TOTAL, total);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-application-list",
          text: ERROR_MESSAGES.LOAD_APPLICATION_LIST_ERROR,
          error,
        },
        { root: true }
      );
    }
    commit(APPLICATION_MUTATIONS.TOGGLE_IS_LOADING, false);
  },
  async [APPLICATION_ACTIONS.LOAD_COMPLETED_APPLICATION_LIST](
    { commit, dispatch },
    { statuses, pagination }
  ): Promise<void> {
    commit(APPLICATION_MUTATIONS.TOGGLE_IS_LOADING, true);

    try {
      let data = [];
      let total = 0;

      // Don't bother searching if no statuses are provided.
      if (statuses && statuses.length > 0) {
        const response = await api.getApplications(statuses, pagination);
        data = response.data.data;
        total = response.data.pagination.total;
      }

      commit(APPLICATION_MUTATIONS.SET_COMPLETED_APPLICATION_LIST, data);
      commit(APPLICATION_MUTATIONS.SET_COMPLETED_APPLICATION_LIST_TOTAL, total);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-completed-application-list",
          text: ERROR_MESSAGES.LOAD_APPLICATION_LIST_ERROR,
          error,
        },
        { root: true }
      );
    }
    commit(APPLICATION_MUTATIONS.TOGGLE_IS_LOADING, false);
  },
  async [APPLICATION_ACTIONS.LOAD_MY_APPLICATION_LIST](
    { commit, dispatch },
    { statuses, pagination }
  ): Promise<void> {
    commit(APPLICATION_MUTATIONS.TOGGLE_IS_LOADING, true);

    try {
      let data = [];
      let total = 0;

      // Don't bother searching if no statuses are provided.
      if (statuses && statuses.length > 0) {
        const response = await api.getMyApplications(statuses, pagination);
        data = response.data.data;
        total = response.data.pagination.total;
      }

      commit(APPLICATION_MUTATIONS.SET_MY_APPLICATION_LIST, data);
      commit(APPLICATION_MUTATIONS.SET_MY_APPLICATION_LIST_TOTAL, total);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-my-applications",
          text: ERROR_MESSAGES.LOAD_MY_APPLICATION_LIST_ERROR,
          error,
        },
        { root: true }
      );
    }

    commit(APPLICATION_MUTATIONS.TOGGLE_IS_LOADING, false);
  },
  async [APPLICATION_ACTIONS.LOAD_DRAFT_LIST](
    { commit, dispatch },
    { pagination }
  ): Promise<void> {
    try {
      const response = await api.getDrafts(pagination);

      commit(APPLICATION_MUTATIONS.SET_DRAFTS_LIST, response.data.data);
      commit(
        APPLICATION_MUTATIONS.SET_DRAFTS_LIST_TOTAL,
        response.data.pagination.total
      );
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-draft-applications",
          text: ERROR_MESSAGES.LOAD_DRAFT_LIST_ERROR,
          error,
        },
        { root: true }
      );
    }
  },
  async [APPLICATION_ACTIONS.LOAD_APPLICATION](
    { commit, dispatch },
    applicationId
  ): Promise<void> {
    commit(APPLICATION_MUTATIONS.TOGGLE_IS_LOADING, true);
    // clear out existing application data
    commit(APPLICATION_MUTATIONS.SET_APPLICATION_DATA, null);

    try {
      const response = await api.getApplication(applicationId);
      commit(APPLICATION_MUTATIONS.SET_IFCS_DATA, response.ifcsDetails);
      commit(APPLICATION_MUTATIONS.SET_APPLICATION_DATA, response);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-application-data",
          text: ERROR_MESSAGES.LOAD_APPLICATION_ERROR,
          error,
        },
        { root: true }
      );
    }
    commit(APPLICATION_MUTATIONS.TOGGLE_IS_LOADING, false);
  },
  [APPLICATION_ACTIONS.PRE_POPULATE_ADDRESS]({ commit, state }) {
    const applicationData = state.applicationData;

    if (!applicationData) {
      return;
    }

    if (applicationData.finalDetails) {
      // prepopulate card delivery address
      commit(
        APPLICATION_MUTATIONS.SET_DELIVERY_ADDRESS,
        applicationData.finalDetails.deliveryAddress || emptyAddress
      );
      commit(
        APPLICATION_MUTATIONS.SET_DELIVERY_ADDRESS_TEXT,
        applicationData.finalDetails.deliveryAddressText || ""
      );
      commit(
        ADDRESS_LOOKUP.NS(ADDRESS_LOOKUP.MUTATIONS.SET_RESPONSE_BY_KEY),
        {
          key: "delivery",
          data: [applicationData.finalDetails.deliveryAddress] || [],
        },
        {
          root: true,
        }
      );
    }
    if (applicationData.businessDetails) {
      // prepopulate card physical address
      commit(
        APPLICATION_MUTATIONS.SET_PHYSICAL_ADDRESS,
        applicationData.businessDetails.physicalAddress || emptyAddress
      );
      commit(
        APPLICATION_MUTATIONS.SET_PHYSICAL_ADDRESS_TEXT,
        applicationData.businessDetails.physicalAddressText || ""
      );
      commit(
        ADDRESS_LOOKUP.NS(ADDRESS_LOOKUP.MUTATIONS.SET_RESPONSE_BY_KEY),
        {
          key: "physical",
          data: [applicationData.businessDetails.physicalAddress] || [],
        },
        {
          root: true,
        }
      );
    }
    // prepopulate card residential address
    if (applicationData.creditDetails) {
      commit(
        APPLICATION_MUTATIONS.SET_RESIDENTIAL_ADDRESS,
        applicationData.creditDetails.residentialAddress || emptyAddress
      );
      commit(
        APPLICATION_MUTATIONS.SET_RESIDENTIAL_ADDRESS_TEXT,
        applicationData.creditDetails.residentialAddressText || ""
      );
      commit(
        ADDRESS_LOOKUP.NS(ADDRESS_LOOKUP.MUTATIONS.SET_RESPONSE_BY_KEY),
        {
          key: "residential",
          data: [applicationData.creditDetails.residentialAddress] || [],
        },
        {
          root: true,
        }
      );
    }
  },
  async [APPLICATION_ACTIONS.UPDATE_APPLICATION](
    { commit, state, dispatch },
    { id, applicationData }
  ) {
    commit(APPLICATION_MUTATIONS.TOGGLE_IS_LOADING, true);
    try {
      await api.updateApplication(id, applicationData);
      // set the applicationDataOriginal state so hasChanges is false
      commit(APPLICATION_MUTATIONS.SET_APPLICATION_DATA, state.applicationData);

      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "successfully-updated-application",
          text: `Successfully updated application`,
        },
        { root: true }
      );
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-update-application-data",
          text: ERROR_MESSAGES.UPDATE_APPLICATION_ERROR,
          error,
        },
        { root: true }
      );
    }
    commit(APPLICATION_MUTATIONS.TOGGLE_IS_LOADING, false);
  },
  async [APPLICATION_ACTIONS.UPDATE_IFCS_DETAILS](
    { commit, state, dispatch },
    { id, ifcsData }
  ) {
    commit(APPLICATION_MUTATIONS.TOGGLE_IS_LOADING, true);
    try {
      await api.updateIfcsDetails(id, ifcsData);
      // set the original state so hasChanges is false
      commit(APPLICATION_MUTATIONS.SET_IFCS_DATA, state.ifcsOverrideData);

      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "successfully-updated-application",
          text: `Successfully updated application`,
        },
        { root: true }
      );
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-update-application-data",
          text: ERROR_MESSAGES.UPDATE_APPLICATION_ERROR,
          error,
        },
        { root: true }
      );
    }
    commit(APPLICATION_MUTATIONS.TOGGLE_IS_LOADING, false);
  },
  async [APPLICATION_ACTIONS.CHANGE_CREDIT_LIMIT](
    { commit, state, dispatch },
    { id, creditLimit }
  ) {
    commit(APPLICATION_MUTATIONS.TOGGLE_IS_LOADING, true);
    try {
      await api.changeCreditLimit(id, creditLimit);

      commit(
        APPLICATION_MUTATIONS.SET_CREDIT_LIMIT,
        state.applicationData && state.applicationData.creditLimit
      );

      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "successfully-updated-credit-limit",
          text: `Successfully updated credit limit`,
        },
        { root: true }
      );
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-update-credit-limit",
          text: ERROR_MESSAGES.CHANGE_CREDIT_LIMIT_ERROR,
          error,
        },
        { root: true }
      );
    }
    commit(APPLICATION_MUTATIONS.TOGGLE_IS_LOADING, false);
  },
  async [APPLICATION_ACTIONS.CANCEL_APPLICATION](
    { commit, dispatch },
    { id, reason }
  ) {
    let success = false;
    commit(APPLICATION_MUTATIONS.TOGGLE_IS_LOADING, true);
    try {
      await api.cancelApplication(id, reason);
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "successfully-cancelled-application",
          text: `Application cancelled `,
        },
        { root: true }
      );
      commit(APPLICATION_MUTATIONS.SET_STATUS, "Cancelled");
      commit(APPLICATION_MUTATIONS.SET_CANCELLATION_REASON, reason);
      success = true;
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-cancel-application",
          text: ERROR_MESSAGES.CANCEL_APPLICATION_ERROR,
          error,
        },
        { root: true }
      );
    }
    commit(APPLICATION_MUTATIONS.TOGGLE_IS_LOADING, false);
    return success;
  },
  [APPLICATION_ACTIONS.SHOW_REQUEST_FOR_CALLBACK]({ commit }, task): void {
    commit(APPLICATION_MUTATIONS.SET_REQUEST_FOR_CALLBACK_TASK, task);
  },
  [APPLICATION_ACTIONS.SHOW_CREDIT_OUTCOME]({ commit }, task): void {
    commit(APPLICATION_MUTATIONS.SET_CREDIT_OUTCOME_TASK, task);
  },
  [APPLICATION_ACTIONS.SHOW_FOLLOW_UP_PERSONAL_GUARANTEE](
    { commit },
    task: ITimelineItem
  ): void {
    commit(APPLICATION_MUTATIONS.SET_FOLLOW_UP_PERSONAL_GUARANTEE_TASK, task);
  },
  [APPLICATION_ACTIONS.SHOW_CARD_ACCOUNT_SETUP](
    { commit },
    task: ITimelineItem
  ): void {
    commit(APPLICATION_MUTATIONS.SET_CARD_ACCOUNT_SETUP_TASK, task);
  },
  [APPLICATION_ACTIONS.SHOW_MODIFY_ACCOUNT_NUMBER](
    { commit },
    task: ITimelineItem
  ): void {
    commit(APPLICATION_MUTATIONS.SET_MODIFY_ACCOUNT_NUMBER_TASK, task);
  },
  [APPLICATION_ACTIONS.SHOW_JDE_ACCOUNT_SETUP](
    { commit },
    task: ITimelineItem
  ): void {
    commit(APPLICATION_MUTATIONS.SET_JDE_ACCOUNT_SETUP_TASK, task);
  },
  async [APPLICATION_ACTIONS.DECLINE_CALLBACK_OUTCOME](
    { dispatch },
    { taskId, comment }
  ) {
    let success = false;
    try {
      await api.declineCallBackOutcome(taskId, comment);
      success = true;
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-decline-application",
          text: ERROR_MESSAGES.DECLINE_CALLBACK_OUTCOME_ERROR,
          error,
        },
        { root: true }
      );
    }
    return success;
  },
  async [APPLICATION_ACTIONS.CREDIT_TEAM_DECLINE_APPLICATION](
    { dispatch },
    { taskId, creditDecisionChangeReason }
  ) {
    let success = false;
    try {
      await api.creditTeamDeclined(taskId, creditDecisionChangeReason);
      success = true;
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-credit-team-decline-application",
          text: ERROR_MESSAGES.DECLINE_CALLBACK_OUTCOME_ERROR,
          error,
        },
        { root: true }
      );
    }
    return success;
  },
  async [APPLICATION_ACTIONS.CREDIT_TEAM_APPROVE_APPLICATION](
    { dispatch },
    { taskId, creditDecisionChangeReason, creditLimit }
  ) {
    let success = false;
    try {
      await api.creditTeamApproved(
        taskId,
        creditDecisionChangeReason,
        creditLimit
      );
      success = true;
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-credit-team-approve-application",
          text: ERROR_MESSAGES.CREDIT_TEAM_APPROVE_APPLICATION_ERROR,
          error,
        },
        { root: true }
      );
    }
    return success;
  },
  async [APPLICATION_ACTIONS.CREDIT_TEAM_NEED_PG](
    { dispatch },
    { taskId, creditDecisionChangeReason, creditLimit }
  ) {
    let success = false;
    try {
      await api.creditTeamNeedPg(
        taskId,
        creditDecisionChangeReason,
        creditLimit
      );
      success = true;
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-credit-team-need-pg",
          text: ERROR_MESSAGES.CREDIT_TEAM_NEED_PG_ERROR,
          error,
        },
        { root: true }
      );
    }
    return success;
  },
  async [APPLICATION_ACTIONS.COMPLETED_APPLICATION](
    { dispatch },
    { taskId, comment }
  ) {
    let success = false;
    try {
      await api.completeCallBackOutcome(taskId, comment);
      success = true;
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-complete-application",
          text: ERROR_MESSAGES.COMPLETED_APPLICATION_ERROR,
          error,
        },
        { root: true }
      );
    }
    return success;
  },
  async [APPLICATION_ACTIONS.LOAD_PHYSICAL_ADDRESS_DETAILS](
    { commit },
    physicalAddressDpid
  ) {
    let physicalDetails = {} as any;
    if (physicalAddressDpid) {
      commit(
        APPLICATION_MUTATIONS.SET_PHYSICAL_ADDRESS_LOOKUP_IS_LOADING,
        true
      );
      try {
        const response = await api.addressDetails(physicalAddressDpid);
        physicalDetails = response.data;
      } catch (error: any) {
        console.error("Failed to load physical address details");
      }
    }
    commit(APPLICATION_MUTATIONS.SET_PHYSICAL_ADDRESS_LOOKUP, physicalDetails);
    commit(APPLICATION_MUTATIONS.SET_PHYSICAL_ADDRESS_LOOKUP_IS_LOADING, false);
  },
  async [APPLICATION_ACTIONS.LOAD_DELIVERY_ADDRESS_DETAILS](
    { commit },
    deliveryAddressDpid
  ) {
    let deliveryDetails = {};
    if (deliveryAddressDpid) {
      commit(
        APPLICATION_MUTATIONS.SET_DELIVERY_ADDRESS_LOOKUP_IS_LOADING,
        true
      );
      try {
        const response = await api.addressDetails(deliveryAddressDpid);
        deliveryDetails = response.data;
      } catch (error: any) {
        console.error("Failed to load delivery address details");
      }
    }
    commit(APPLICATION_MUTATIONS.SET_DELIVERY_ADDRESS_LOOKUP, deliveryDetails);
    commit(APPLICATION_MUTATIONS.SET_DELIVERY_ADDRESS_LOOKUP_IS_LOADING, false);
  },
  async [APPLICATION_ACTIONS.PERSONAL_GUARANTEE_OBTAINED](
    { dispatch },
    { taskId, obtainedByEmailOrPost, fileLocation }
  ) {
    let success = false;
    try {
      await api.personalGuaranteeObtained(
        taskId,
        obtainedByEmailOrPost,
        fileLocation
      );
      success = true;
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-personal-guarantee-obtained",
          text: ERROR_MESSAGES.UPDATE_TASK_ERROR,
          error,
        },
        { root: true }
      );
    }
    return success;
  },
  async [APPLICATION_ACTIONS.CARD_ACCOUNT_SET_UP_COMPLETE](
    { dispatch },
    { taskId, accountNumber }
  ) {
    let success = false;
    try {
      await api.cardAccountSetup(taskId, accountNumber);
      success = true;
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-card-account-setup",
          text: ERROR_MESSAGES.UPDATE_TASK_ERROR,
          error,
        },
        { root: true }
      );
    }
    return success;
  },
  async [APPLICATION_ACTIONS.MODIFY_ACCOUNT_NUMBER_COMPLETE](
    { dispatch },
    { taskId, accountNumber }
  ) {
    let success = false;
    try {
      await api.modifyAccountNumber(taskId, accountNumber);
      success = true;
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-modify-account-number",
          text: ERROR_MESSAGES.UPDATE_TASK_ERROR,
          error,
        },
        { root: true }
      );
    }
    return success;
  },
  async [APPLICATION_ACTIONS.JDE_ACCOUNT_SET_UP_COMPLETE](
    { dispatch },
    { taskId, accountNumber }
  ) {
    let success = false;
    try {
      await api.jdeAccountSetup(taskId, accountNumber);
      success = true;
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-jde-account-setup",
          text: ERROR_MESSAGES.UPDATE_TASK_ERROR,
          error,
        },
        { root: true }
      );
    }
    return success;
  },
  [APPLICATION_ACTIONS.SET_MY_APPLICATION_STATUS_FILTERS]({ commit }, value) {
    commit(
      APPLICATION_MUTATIONS.SET_MY_APPLICATIONS_DATA_TABLE_STATUS_FILTERS,
      value
    );
  },
  [APPLICATION_ACTIONS.SET_ALL_APPLICATION_STATUS_FILTERS]({ commit }, value) {
    commit(
      APPLICATION_MUTATIONS.SET_ALL_APPLICATIONS_DATA_TABLE_STATUS_FILTERS,
      value
    );
  },
  [APPLICATION_ACTIONS.SET_COMPLETED_STATUS_FILTERS]({ commit }, value) {
    commit(
      APPLICATION_MUTATIONS.SET_COMPLETED_DATA_TABLE_STATUS_FILTERS,
      value
    );
  },
};
